<template>
  <div class="c-view-invite">
    <BittsLoading
      v-if="loading"
      :is-loading="loading"
      loading-container-class="h-screen"
    />
    <template v-else>
      <BittsCard
        class="c-view-invite__card"
        :class="{
          'pt-80': !isSomeoneElses,
          'c-view-invite__card--no-border': !showInvite,
        }"
        :no-padding="false"
        :drop-shadow="!!showInvite"
      >
        <AccessDenied
          v-if="isSomeoneElses"
          data-testid="access-denied"
          :deny-message="denyMessage"
          :show-admins="false"
        />
        <div
          v-else-if="isLoggedIn && showInvite"
          class="c-view-invite__card__content"
        >
          <!-- TODO: Get actual logo -->
          <BittsAvatar
            v-if="clearbitCompany"
            size="large"
            shape="square"
            :org="clearbitCompany"
            class="c-view-invite__card__avatar"
          />
          <h1 class="title">
            Join {{ invite?.organization.name }} on Crossbeam
          </h1>
          <p class="explainer">
            You've been invited to join your team on Crossbeam, the biggest
            ecosystem-led growth platform. Don't keep them waiting!
          </p>
          <BittsButton
            size="large"
            class="mt-12"
            text="Accept invitation"
            data-testid="accept-invite-button"
            @click="redeem"
          />
        </div>
        <SeatsOccupied
          v-else-if="isLoggedIn && !showInvite && invite"
          :clearbit-company
          :invite-code="code"
          :invite="invite"
        />
      </BittsCard>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsCard,
  BittsLoading,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import AccessDenied from '@/components/AccessDenied.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useClearbit from '@/composables/useClearbit';
import useIteratively from '@/composables/useIteratively';
import { useFlashesStore } from '@/stores';
import { ClearbitCompany, Invite } from '@/types/invite';
import { switchOrganization } from '@/utils';

import SeatsOccupied from './SeatsOccupied.vue';

const emit = defineEmits(['with-background']);

useHead({ title: 'Join - Crossbeam' });

const flashesStore = useFlashesStore();
const { currentUser, isLoggedIn } = useAuth();

const route = useRoute();
const router = useRouter();

const { searchIcons } = useClearbit(false);
const loading = ref(true);
const invite = ref<Invite | null>(null);
const code = ref(route.params.code as string);
const isSomeoneElses = computed(
  () =>
    isLoggedIn.value &&
    currentUser.value.email?.toLowerCase() !==
      invite.value?.user.email.toLowerCase(),
);

const denyMessage = computed(() => {
  if (!invite.value) return '';
  return (
    `This Invitation is for ${invite.value?.user.email}. ` +
    `You are logged in as ${currentUser.value.email}. ` +
    "Log out and try accepting the invitation again if it's intended for you."
  );
});

const clearbitCompany = ref<ClearbitCompany | null>(null);

const showInvite = computed(() => invite.value?.quota_available !== false);

function isInviteAlreadyRedeemedError(error: unknown): boolean {
  return (
    // @ts-expect-error - our errors are not typed properly
    Array.isArray(error.errors) &&
    // @ts-expect-error - our errors are not typed properly
    error.errors[0] === 'This invite has already been redeemed'
  );
}

onMounted(async () => {
  const { data, error, response } = await crossbeamApi.GET(
    '/v0.1/user-invites/{invite_code}',
    {
      params: { path: { invite_code: code.value } },
    },
  );
  if (error) {
    const status = (response as Response | undefined)?.status;
    if (status === 400 && isInviteAlreadyRedeemedError(error)) {
      loading.value = false;
      await router.push({ name: 'home' });
      return;
    }
    flashesStore.addUnhandledError(new Error(error));
    return;
  }
  invite.value = data;
  if (!isLoggedIn.value && !isSomeoneElses.value && invite.value) {
    const invitingOrgString = JSON.stringify({
      ...invite.value.organization,
      email: invite.value.user.email,
    });
    await router.push({
      name: 'register-public-params',
      params: {
        invitingOrgString,
        inviteNextUrl: route.fullPath,
      },
    });
    return;
  }
  // Only emit background for the main invite view, not for seats occupied
  if (showInvite.value) {
    emit('with-background');
  }

  /* Get logo, if it exists in clearbit */
  if (invite.value) {
    try {
      const companyResults = await searchIcons(invite.value.organization.name);
      const currentInvite = invite.value;
      clearbitCompany.value =
        (companyResults as ClearbitCompany[]).find(
          (c) => c.name === currentInvite.organization.name,
        ) || null;
    } catch (err) {
      flashesStore.addUnhandledError(
        err instanceof Error ? err : new Error(String(err)),
      );
    }
  }
  loading.value = false;
});

const { iteratively } = useIteratively();
async function redeem() {
  const { error } = await crossbeamApi.POST(
    '/v0.1/user-invites/{invite_code}/redeem',
    {
      params: { path: { invite_code: code.value } },
    },
  );
  if (error) {
    flashesStore.addUnhandledError(new Error(error));
    return;
  }
  iteratively.userRedeemsInvite({
    event_site: EVENT_SITES.REDEEM_INVITE_BUTTON,
    source: (route.query?.source as string) || 'crossbeam-web',
  });
  switchOrganization(router, invite.value?.organization.id);
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-view-invite {
  @apply px-16;
}
.c-view-invite__card {
  @apply flex flex-col items-center max-w-[600px] mx-auto p-40 relative mt-72 sm:mt-0;
  .c-view-invite__card__content {
    @apply flex flex-col items-center;
  }
}
.c-view-invite__card--no-border {
  @apply border-0 shadow-none;
}
.c-view-invite__card__avatar {
  @apply absolute top-[-48px] outline outline-8 outline-white rounded-16 border-none;
}
.title {
  @apply text-xl text-neutral-text-strong text-center font-bold mb-16;
}
.explainer {
  @apply text-neutral-text-weak text-m mb-40 text-center;
}
</style>
<style lang="pcss">
@reference "../../css/theme.css";
.c-app-bare.c-app-bare__view_invite__background {
  background: radial-gradient(
    200.73% 104.41% at 50% 120.59%,
    theme(colors.brand-teal) 3.5%,
    theme(colors.secondary-accent) 70.74%
  );
  .c-app-bare__content {
    @apply justify-start sm:justify-center;
  }
}
</style>
